import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../BaseComponents/Modal';
import TextInput from '../../../BaseComponents/TextInput';
import MultiSelect from '../../../BaseComponents/MultiSelect';
import Button from '../../../BaseComponents/Button';
import { PanelWithDetails, CreatePanelRequest, UpdatePanelRequest, createPanel, updatePanel } from '../../../../api/panels';
import { getBodySystemsList } from '../../../../api/body-systems';
import { getBiomarkersList } from '../../../../api/biomarkers';
import { BodySystem } from '../../../../api/conditions';
import './PanelFormModal.scss';

interface Biomarker {
  id: number;
  name: string;
}

interface PanelFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  panelToEdit?: PanelWithDetails | null;
  onPanelSaved: () => void;
}

const PanelFormModal: React.FC<PanelFormModalProps> = ({ 
  isOpen, 
  onClose, 
  panelToEdit, 
  onPanelSaved 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    catalog_id: '',
    body_system_ids: [] as number[],
    body_systems: [] as { key: string; value: string }[],
    biomarker_ids: [] as number[],
    biomarkers: [] as { key: string; value: string }[],
    price: '',
    non_discounted_price: ''
  });

  const [bodySystemOptions, setBodySystemOptions] = useState<{ key: string; value: string }[]>([]);
  const [biomarkerOptions, setBiomarkerOptions] = useState<{ key: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFormData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const [bodySystemsResponse, biomarkersResponse] = await Promise.all([
        getBodySystemsList(),
        getBiomarkersList()
      ]);
      
      const bodySystemOptions = (bodySystemsResponse.body_systems || []).map((bs: BodySystem) => ({
        key: bs.id.toString(),
        value: bs.title
      }));
      const biomarkerOptions = (biomarkersResponse.biomarkers || []).map((bm: Biomarker) => ({
        key: bm.id.toString(),
        value: bm.name
      }));
      
      setBodySystemOptions(bodySystemOptions);
      setBiomarkerOptions(biomarkerOptions);

      if (panelToEdit) {
        const selectedBodySystems = panelToEdit.body_systems.map(bs => ({
          key: bs.id.toString(),
          value: bs.title
        }));
        const selectedBiomarkers = panelToEdit.biomarkers.map(bm => ({
          key: bm.id.toString(),
          value: bm.name
        }));
        
        setFormData({
          name: panelToEdit.name,
          catalog_id: panelToEdit.catalog_id || '',
          body_system_ids: panelToEdit.body_systems.map(bs => bs.id),
          body_systems: selectedBodySystems,
          biomarker_ids: panelToEdit.biomarkers.map(bm => bm.id),
          biomarkers: selectedBiomarkers,
          price: panelToEdit.price?.toString() || '',
          non_discounted_price: (panelToEdit.non_discounted_price ?? panelToEdit.non_discounted_price)?.toString() || ''
        });
      } else {
        setFormData({
          name: '',
          catalog_id: '',
          body_system_ids: [],
          body_systems: [],
          biomarker_ids: [],
          biomarkers: [],
          price: '',
          non_discounted_price: ''
        });
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      setError('Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  }, [panelToEdit]);

  useEffect(() => {
    if (isOpen) {
      loadFormData();
    }
  }, [isOpen, loadFormData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'price' || name === 'non_discounted_price') {
      if (value.includes('-') || (value !== '' && Number(value) < 0)) {
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const handleMultiSelectChange = (option: { key: string; value: string }, fieldName: string) => {
    if (fieldName === 'body_systems') {
      const currentBodySystems = formData.body_systems || [];
      const existingIndex = currentBodySystems.findIndex(bs => bs.key === option.key);
      
      let newBodySystems: { key: string; value: string }[];
      if (existingIndex >= 0) {
        newBodySystems = currentBodySystems.filter((_, index) => index !== existingIndex);
      } else {
        newBodySystems = [...currentBodySystems, option];
      }
      
      setFormData(prev => ({
        ...prev,
        body_systems: newBodySystems,
        body_system_ids: newBodySystems.map((bs: { key: string; value: string }) => parseInt(bs.key))
      }));
    } else if (fieldName === 'biomarkers') {
      const currentBiomarkers = formData.biomarkers || [];
      const existingIndex = currentBiomarkers.findIndex(bm => bm.key === option.key);
      
      let newBiomarkers: { key: string; value: string }[];
      if (existingIndex >= 0) {
        newBiomarkers = currentBiomarkers.filter((_, index) => index !== existingIndex);
      } else {
        newBiomarkers = [...currentBiomarkers, option];
      }
      
      setFormData(prev => ({
        ...prev,
        biomarkers: newBiomarkers,
        biomarker_ids: newBiomarkers.map((bm: { key: string; value: string }) => parseInt(bm.key))
      }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    const price = formData.price ? parseFloat(formData.price) : 0;
    const nonDiscountedPrice = formData.non_discounted_price ? parseFloat(formData.non_discounted_price) : 0;

    if (price <= 0 || nonDiscountedPrice <= 0) {
      setError('Price and Non Discounted Price must be greater than 0');
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (panelToEdit) {
        const updateData: UpdatePanelRequest = {
          id: panelToEdit.id,
          name: formData.name.trim(),
          catalog_id: formData.catalog_id.trim() || '',
          body_system_ids: formData.body_system_ids,
          biomarker_ids: formData.biomarker_ids,
          price,
          non_discounted_price: nonDiscountedPrice
        };
        await updatePanel(updateData);
      } else {
        const panelData: CreatePanelRequest = {
          name: formData.name.trim(),
          catalog_id: formData.catalog_id.trim() || '',
          body_system_ids: formData.body_system_ids,
          biomarker_ids: formData.biomarker_ids,
          price,
          non_discounted_price: nonDiscountedPrice
        };
        await createPanel(panelData);
      }

      onPanelSaved();
    } catch (error: any) {
      console.error('Error saving panel:', error);
      setError(error?.response?.data?.detail || error?.message || 'Failed to save panel');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={panelToEdit ? 'Edit Panel' : 'Add Panel'}
      width="600px"
    >
      {isLoading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="panel-form-modal">
          <div className="form-group">
            <TextInput
              name="name"
              title="Name *"
              placeholder="Enter panel name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              isError={!!error && !formData.name.trim()}
              errorMessage={error && !formData.name.trim() ? error : undefined}
            />
          </div>

          <div className="form-group">
            <TextInput
              name="catalog_id"
              title="Catalog ID"
              placeholder="Enter catalog ID"
              type="text"
              value={formData.catalog_id}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <MultiSelect
              name="body_systems"
              title="Body Systems"
              options={bodySystemOptions}
              selectedOptions={formData.body_systems}
              onChange={handleMultiSelectChange}
              placeholder="Select body systems"
              isError={false}
            />
          </div>

          <div className="form-group">
            <MultiSelect
              name="biomarkers"
              title="Biomarkers"
              options={biomarkerOptions}
              selectedOptions={formData.biomarkers}
              onChange={handleMultiSelectChange}
              placeholder="Select biomarkers"
              isError={false}
            />
          </div>

          <div className="form-group">
            <TextInput
              name="price"
              title="Price"
              placeholder="Enter price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              isError={false}
            />
          </div>

          <div className="form-group">
            <TextInput
              name="non_discounted_price"
              title="Non Discounted Price"
              placeholder="Enter non discounted price"
              type="number"
              value={formData.non_discounted_price}
              onChange={handleInputChange}
              isError={false}
            />
          </div>

          {error && (
            <div className="error-message" style={{ color: 'red', marginBottom: '15px' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <Button
              type="submit"
              text={panelToEdit ? 'Update Panel' : 'Add Panel'}
              isLoading={isSubmitting}
              isDisabled={!formData.name.trim()}
            />
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PanelFormModal;

